import type {
  ApiItem,
  ApiCollection,
  Item,
  Collection,
  CreateCollectionRequest,
  UpdateCollectionRequest,
} from '../api/types';

// Transform API item to frontend item format
export function transformApiItem(apiItem: ApiItem): Item {
  return {
    id: apiItem.id.toString(), // Convert number to string for frontend
    title: apiItem.title, // API uses "title"
    description: apiItem.description,
    price_1: parseFloat(apiItem.price_1), // Convert string to number
    price_2: parseFloat(apiItem.price_2), // Convert string to number
    currency: 'NZD', // Default currency - could be made configurable
    image: apiItem.image, // API returns "image", frontend expects "imageUrl"
    url: apiItem.url || '', // May not always be present
    status: apiItem.status,
    visibility: apiItem.visibility,
    tags: apiItem.tags || [], // Use API tags
    systemTags: apiItem.systemTags || [], // Use API tags
    isNew: false, // Could be calculated based on createdAt
    createdAt: apiItem.createdAt,
    updatedAt: apiItem.updatedAt,
    brand: apiItem.brand,
    userCollections: apiItem.userCollections, // Pass through user collections
    engagement: apiItem.engagement, // Pass through engagement data
    userReactions: apiItem.userReactions, // Pass through user reactions
    following: apiItem.following, // Pass through follow status
    data: apiItem.data, // Pass through raw data (country info, gallery, etc.)
  };
}

// Transform API items array to frontend items array
export function transformApiItems(apiItems: ApiItem[]): Item[] {
  return apiItems.map(transformApiItem);
}

// Transform API collection to frontend collection format (keeps API format)
export function transformApiCollection(apiCollection: ApiCollection): Collection {
  const transformedItems = apiCollection.items ? transformApiItems(apiCollection.items) : undefined;

  const result = {
    id: apiCollection.id, // Keep as number for API format
    userId: apiCollection.userId,
    name: apiCollection.name,
    description: apiCollection.description,
    image: apiCollection.image,
    tags: apiCollection.tags,
    isPublic: apiCollection.isPublic,
    createdAt: apiCollection.createdAt,
    updatedAt: apiCollection.updatedAt,
    items: transformedItems,
  };

  return result;
}

// Transform API collection to localStorage format (for backward compatibility)
export function transformApiCollectionToLocalStorage(apiCollection: ApiCollection): {
  id: string;
  name: string;
  visibility: 'public' | 'private';
  productIds: string[];
  createdAt: Date;
} {
  // Extract product IDs from items if available
  const productIds = apiCollection.items
    ? apiCollection.items.map((item) => item.id.toString())
    : [];

  return {
    id: apiCollection.id.toString(), // Convert number to string
    name: apiCollection.name,
    visibility: apiCollection.isPublic ? 'public' : 'private', // Convert boolean to string
    productIds,
    createdAt: new Date(apiCollection.createdAt), // Convert string to Date
  };
}

// Transform localStorage format to API format
export function transformLocalStorageToApi(localCollection: {
  id: string;
  name: string;
  visibility: 'public' | 'private';
  productIds: string[];
  createdAt: Date;
}): Omit<ApiCollection, 'id' | 'userId' | 'createdAt' | 'updatedAt' | 'items'> {
  return {
    name: localCollection.name,
    description: undefined, // localStorage format doesn't have description
    isPublic: localCollection.visibility === 'public', // Convert string to boolean
  };
}

// Transform frontend Collection to API CreateCollectionRequest
export function transformCollectionToCreateRequest(collection: {
  name: string;
  description?: string;
  image?: string | null;
  isPublic?: boolean;
}): CreateCollectionRequest {
  return {
    name: collection.name,
    description: collection.description,
    image: collection.image,
    isPublic: collection.isPublic ?? false,
  };
}

// Transform frontend Collection to API UpdateCollectionRequest
export function transformCollectionToUpdateRequest(collection: {
  name?: string;
  description?: string;
  image?: string | null;
  isPublic?: boolean;
  tags?: string[];
}): UpdateCollectionRequest {
  return {
    name: collection.name,
    description: collection.description,
    image: collection.image,
    isPublic: collection.isPublic,
    tags: collection.tags,
  };
}

// Transform localStorage visibility to API isPublic
export function transformVisibilityToIsPublic(visibility: 'public' | 'private'): boolean {
  return visibility === 'public';
}

// Transform API isPublic to localStorage visibility
export function transformIsPublicToVisibility(isPublic: boolean): 'public' | 'private' {
  return isPublic ? 'public' : 'private';
}

// Transform API collections array to frontend collections array
export function transformApiCollections(apiCollections: ApiCollection[]): Collection[] {
  return apiCollections.map(transformApiCollection);
}

// Transform API collections array to localStorage format (for backward compatibility)
export function transformApiCollectionsToLocalStorage(apiCollections: ApiCollection[]): {
  id: string;
  name: string;
  visibility: 'public' | 'private';
  productIds: string[];
  createdAt: Date;
}[] {
  return apiCollections.map(transformApiCollectionToLocalStorage);
}
